import { hrefFor, type NavigableRouteKey } from '@omnea-app/fe-authz/routeHref'
import { isRouteActive, isRouteExact } from '@omnea-app/fe-routing/routes/isRouteActive'
import type { AccessAnswer } from '@omnea-app/fe-authz/accessRules'
import type { RouteKey } from '@omnea-app/fe-routing/routes/routeKeys'

/** `routeInstance` carries its own href: the route it sits under has no id, so nothing derives it. */
export type MenuItemDestination =
  | { kind: 'route'; routeKey: NavigableRouteKey }
  | { kind: 'routeInstance'; routeKey: RouteKey; href: string }
  | { kind: 'external'; href: string }

export type SidebarSectionItem = {
  label: string
  icon: React.ReactElement
  destination: MenuItemDestination
  showsOmneanOnlyBadge?: boolean
}

export type SidebarSection = {
  title: string
  items: SidebarSectionItem[]
}

export const hrefOfMenuItem = ({ destination }: SidebarSectionItem): string => {
  switch (destination.kind) {
    case 'route':
      return hrefFor(destination.routeKey)
    case 'routeInstance':
    case 'external':
      return destination.href
  }
}

export const isMenuItemVisible = ({ destination }: SidebarSectionItem, access: ReadonlyMap<RouteKey, AccessAnswer>): boolean => {
  switch (destination.kind) {
    case 'route':
    case 'routeInstance':
      return access.get(destination.routeKey) === 'allowed'
    case 'external':
      return true
  }
}

export const recordHrefsOf = (sections: readonly SidebarSection[]): readonly string[] =>
  sections.flatMap((section) =>
    section.items.filter((item) => item.destination.kind === 'routeInstance').map((item) => hrefOfMenuItem(item))
  )

/** A section that lists records yields to the open record — a prefix rule would light both. */
export const isMenuItemSelected = (item: SidebarSectionItem, currentPath: string, recordHrefs: readonly string[]): boolean => {
  const href = hrefOfMenuItem(item)

  if (item.destination.kind === 'external') return false
  if (item.destination.kind === 'routeInstance') return isRouteActive(currentPath, href)

  const ownedByRecord = recordHrefs.some((recordHref) => isRouteActive(currentPath, recordHref))

  return ownedByRecord ? isRouteExact(currentPath, href) : isRouteActive(currentPath, href)
}
